import React from "react";

import { SipPhone } from "~/services/calling/sipPhone";
import type { CallState } from "~/context/call/callState";

export type UseConferenceControlRecoveryParams = {
  conferenceNoAnswerTimeoutRef: React.MutableRefObject<number | null>;
  conferenceControlRecoveryUntilRef: React.MutableRefObject<number>;
  stateSnapshotRef: React.MutableRefObject<CallState>;
  sipRef: React.RefObject<SipPhone | null>;
  ensureSipDomain: () => string | null | undefined;
  unhold: () => Promise<void>;
  updateState: (next: Partial<CallState>) => void;
  state: CallState;
};

export type UseConferenceControlRecoveryResult = {
  clearConferenceNoAnswerTimeout: () => void;
  armConferenceControlRecovery: (windowMs?: number) => void;
  scheduleConferenceNoAnswerRecovery: (room: string) => void;
  recoverOperatorCallControl: () => Promise<void>;
};

export function useConferenceControlRecovery({
  conferenceNoAnswerTimeoutRef,
  conferenceControlRecoveryUntilRef,
  stateSnapshotRef,
  sipRef,
  ensureSipDomain,
  unhold,
  updateState,
  state,
}: UseConferenceControlRecoveryParams): UseConferenceControlRecoveryResult {
  const clearConferenceNoAnswerTimeout = React.useCallback(() => {
    if (!conferenceNoAnswerTimeoutRef.current) return;
    window.clearTimeout(conferenceNoAnswerTimeoutRef.current);
    conferenceNoAnswerTimeoutRef.current = null;
  }, [conferenceNoAnswerTimeoutRef]);

  const armConferenceControlRecovery = React.useCallback(
    (windowMs = 45_000) => {
      conferenceControlRecoveryUntilRef.current = Date.now() + windowMs;
    },
    [conferenceControlRecoveryUntilRef]
  );

  const recoverOperatorCallControl = React.useCallback(async () => {
    let latest = stateSnapshotRef.current;
    if (latest.sipStatus !== "registered") return;
    if (!latest.conferenceRoom) return;
    if (latest.callStatus === "active" && !latest.isOnHold) return;

    if (latest.callStatus === "held" || latest.isOnHold) {
      await unhold();
      latest = stateSnapshotRef.current;
      if (!latest.conferenceRoom) return;
      if (latest.callStatus === "active" && !latest.isOnHold) return;
    }

    if (
      latest.callStatus !== "idle" &&
      latest.callStatus !== "ended" &&
      latest.callStatus !== "dialing" &&
      latest.callStatus !== "held" &&
      !latest.isOnHold
    ) {
      return;
    }

    const sipDomain = ensureSipDomain();
    if (!sipDomain) return;

    try {
      updateState({
        callStatus: "dialing",
        callDirection: "outgoing",
        isOnHold: false,
      });
      await sipRef.current?.joinRoom(latest.conferenceRoom, sipDomain);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Unable to recover active conference call.";
      updateState({ lastError: message });
    }
  }, [
    ensureSipDomain,
    sipRef,
    stateSnapshotRef,
    unhold,
    updateState,
  ]);

  const scheduleConferenceNoAnswerRecovery = React.useCallback(
    (room: string) => {
      clearConferenceNoAnswerTimeout();
      conferenceNoAnswerTimeoutRef.current = window.setTimeout(() => {
        conferenceNoAnswerTimeoutRef.current = null;
        const latest = stateSnapshotRef.current;
        if (!latest.conferenceRoom || latest.conferenceRoom !== room) return;
        if (latest.callStatus === "active" && !latest.isOnHold) return;

        void (async () => {
          await unhold();

          let refreshed = stateSnapshotRef.current;
          if (
            !refreshed.conferenceRoom ||
            refreshed.conferenceRoom !== room ||
            (refreshed.callStatus === "active" && !refreshed.isOnHold)
          ) {
            return;
          }

          if (refreshed.sipStatus === "registered") {
            const sipDomain = ensureSipDomain();
            if (sipDomain) {
              try {
                updateState({
                  callStatus: "dialing",
                  callDirection: "outgoing",
                  isOnHold: false,
                });
                await sipRef.current?.joinRoom(room, sipDomain);
              } catch (error) {
                const message =
                  error instanceof Error && error.message
                    ? error.message
                    : "Unable to recover active conference call.";
                updateState({ lastError: message, isOnHold: false });
              }
            }
          }

          refreshed = stateSnapshotRef.current;
          if (
            refreshed.conferenceRoom === room &&
            (refreshed.callStatus === "held" ||
              refreshed.callStatus === "dialing" ||
              refreshed.isOnHold)
          ) {
            updateState({
              isOnHold: false,
              callStatus: "active",
            });
          }
        })();
      }, 8_000);
    },
    [
      clearConferenceNoAnswerTimeout,
      conferenceNoAnswerTimeoutRef,
      ensureSipDomain,
      sipRef,
      stateSnapshotRef,
      unhold,
      updateState,
    ]
  );

  React.useEffect(() => {
    if (!state.conferenceRoom) return;
    if (Date.now() > conferenceControlRecoveryUntilRef.current) return;
    if (state.callStatus === "active" && !state.isOnHold) return;

    const timer = window.setTimeout(() => {
      void recoverOperatorCallControl();
    }, 600);

    return () => window.clearTimeout(timer);
  }, [
    conferenceControlRecoveryUntilRef,
    recoverOperatorCallControl,
    state.conferenceRoom,
    state.callStatus,
    state.isOnHold,
  ]);

  React.useEffect(() => {
    if (state.conferenceRoom) return;
    clearConferenceNoAnswerTimeout();
    conferenceControlRecoveryUntilRef.current = 0;
  }, [
    clearConferenceNoAnswerTimeout,
    conferenceControlRecoveryUntilRef,
    state.conferenceRoom,
  ]);

  return {
    clearConferenceNoAnswerTimeout,
    armConferenceControlRecovery,
    scheduleConferenceNoAnswerRecovery,
    recoverOperatorCallControl,
  };
}
